import { useCallback, useEffect, useRef, useState } from "react";

import { getAdminSettings } from "@/lib/admin-settings";

interface UseAutosaveOptions<T> {
  /** The data to track for changes */
  data: T;
  /** Function to call when saving */
  onSave: () => Promise<void>;
  /** Whether autosave is enabled (e.g., disable for new items that haven't been created yet) */
  enabled?: boolean;
  /** Minimum time between saves in ms (prevents rapid saves) */
  minInterval?: number;
}

interface UseAutosaveReturn {
  /** Timestamp of last successful save */
  lastSaved: Date | null;
  /** Whether a save is currently in progress */
  isSaving: boolean;
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
  /** Manually trigger a save */
  saveNow: () => Promise<void>;
  /** Mark current data as saved (e.g., after manual save) */
  markAsSaved: () => void;
}

export function useAutosave<T>({
  data,
  onSave,
  enabled = true,
  minInterval = 5000,
}: UseAutosaveOptions<T>): UseAutosaveReturn {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Keep track of the last saved data to detect changes
  const lastSavedDataRef = useRef<string | null>(null);
  const lastSaveTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const settingsIntervalRef = useRef<number>(getAdminSettings().autosaveInterval);

  // Serialize data for comparison
  const serializedData = JSON.stringify(data);

  // Check for unsaved changes whenever data changes
  useEffect(() => {
    if (lastSavedDataRef.current !== null && serializedData !== lastSavedDataRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [serializedData]);

  // Initialize lastSavedDataRef on first render with data
  useEffect(() => {
    if (lastSavedDataRef.current === null && enabled) {
      lastSavedDataRef.current = serializedData;
    }
  }, [serializedData, enabled]);

  const performSave = useCallback(async () => {
    // Don't save if no changes or already saving
    if (!hasUnsavedChanges || isSaving) {
      return;
    }

    // Respect minimum interval between saves
    const now = Date.now();
    if (now - lastSaveTimeRef.current < minInterval) {
      return;
    }

    setIsSaving(true);

    try {
      await onSave();
      lastSavedDataRef.current = serializedData;
      lastSaveTimeRef.current = Date.now();
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Autosave failed:", error);
      // Don't update lastSaved on failure
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, isSaving, minInterval, onSave, serializedData]);

  const saveNow = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      await onSave();
      lastSavedDataRef.current = serializedData;
      lastSaveTimeRef.current = Date.now();
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Manual save failed:", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, onSave, serializedData]);

  // Use a ref to always have access to the latest serialized data
  const serializedDataRef = useRef(serializedData);
  serializedDataRef.current = serializedData;

  const markAsSaved = useCallback(() => {
    lastSavedDataRef.current = serializedDataRef.current;
    lastSaveTimeRef.current = Date.now();
    setLastSaved(new Date());
    setHasUnsavedChanges(false);
  }, []);

  // Set up autosave interval
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const setupInterval = () => {
      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      const settings = getAdminSettings();
      settingsIntervalRef.current = settings.autosaveInterval;

      // Don't set up interval if autosave is disabled
      if (settings.autosaveInterval === 0) {
        return;
      }

      intervalRef.current = setInterval(() => {
        performSave();
      }, settings.autosaveInterval);
    };

    setupInterval();

    // Listen for settings changes
    const handleSettingsChange = () => {
      setupInterval();
    };

    window.addEventListener("admin-settings-changed", handleSettingsChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener("admin-settings-changed", handleSettingsChange);
    };
  }, [enabled, performSave]);

  return {
    lastSaved,
    isSaving,
    hasUnsavedChanges,
    saveNow,
    markAsSaved,
  };
}

// Helper function to format "time ago" for the status indicator
export function formatTimeAgo(date: Date | null): string {
  if (!date) return "";

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
